import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import COLOR from 'constants/color';
import {
  VideoList as VideoListIcon,
  PlayCircle,
  TrashOutlined
} from 'assets/icons/revamp';
import YouTube from 'assets/icons/YouTube';
import Vimeo from 'assets/icons/Vimeo';
import { t } from 'services/i18n.service';
import { moreOptionModalVideoURLsSelector } from 'store/reducer/modal/more-options.modal.selectors';
import { updateMoreOptionURLs } from 'store/reducer/modal/slice';
import { MODAL_TYPES } from 'constants/general';
import { isYoutubeURL, isVimeoURL } from 'utils/validators';
import YouTubeMetaData from 'components/shared/YouTubeMetaData';
import { increment } from 'utils/helpers';
import { Video_Metadata } from 'interfaces/setting.interface';

type VideoType = 'youtube' | 'vimeo';

const iconMap = {
  youtube: <YouTube className='~w-8/10 ~h-8/10 flex-shrink-0' />,
  vimeo: <Vimeo className='~w-8/10 ~h-8/10 flex-shrink-0' />
};

const renderVideoInfo = (
  metadata: Video_Metadata | undefined,
  index: number,
  isYouTubeURLMetadataUpdating: boolean
) => {
  if (isYouTubeURLMetadataUpdating && !metadata) {
    return (
      <div className='w-3 h-3 rounded-full border-r border-gray-600 dark:border-white animate-spin ~mb-1/2' />
    );
  }

  if (metadata && metadata.title && metadata.duration) {
    return (
      <div className='~mb-0.5/1'>
        <YouTubeMetaData title={metadata.title} duration={metadata.duration} />
      </div>
    );
  }

  if (metadata && metadata.title) {
    return (
      <h4 className='~text-sm/base font-medium text-black dark:text-white truncate ~mb-0.5/1'>
        {metadata.title}
      </h4>
    );
  }

  return (
    <h4 className='~text-sm/base font-medium text-black dark:text-white truncate ~mb-0.5/1'>
      {t('tabs_revamp.videos_tab.video')} {increment(index)}
    </h4>
  );
};

const VideoList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { video_urls, isYouTubeURLMetadataUpdating, videos_metadata } =
    useAppSelectorShallow(moreOptionModalVideoURLsSelector);

  const detectVideoType = (url: string): VideoType => {
    if (isYoutubeURL(url)) {
      return 'youtube';
    }
    if (isVimeoURL(url)) {
      return 'vimeo';
    }
    return 'youtube'; // default
  };

  const getVideoMetadata = (url: string) => {
    return videos_metadata.find((meta) => meta.video_url === url);
  };

  const handleDeleteVideo = (urlToDelete: string) => {
    const newUrls = video_urls.filter((url) => url !== urlToDelete);
    dispatch(
      updateMoreOptionURLs({
        modal_type: MODAL_TYPES.VIDEOS_URLS,
        urls: newUrls
      })
    );
  };

  const handlePreviewVideo = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <div className='flex flex-col ~gap-1/2'>
      {video_urls.length > 0 ? (
        <>
          <div className='flex items-center ~gap-2/3'>
            <VideoListIcon
              styles='~w-4/5 ~h-4/5'
              fill={COLOR.PRIMARY.LIGHT_900}
            />
            <h3 className='~text-xs/sm font-semibold text-black dark:text-white'>
              {video_urls.length}
              {video_urls.length === 1
                ? t('tabs_revamp.videos_tab.video')
                : t('tabs_revamp.videos_tab.videos')}
              {t('tabs_revamp.videos_tab.videos_added')}
            </h3>
          </div>

          <div className='flex flex-col ~gap-3/4'>
            {video_urls.map((url, index) => {
              const videoType = detectVideoType(url);
              const metadata = getVideoMetadata(url);

              return (
                <div
                  key={`${url}-${index}`}
                  className='flex items-start ~gap-3/4 ~p-3/4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700'>
                  <div className='flex-shrink-0 mt-0.5'>
                    {iconMap[videoType]}
                  </div>

                  <div className='flex-1 min-w-0'>
                    {renderVideoInfo(
                      metadata,
                      index,
                      isYouTubeURLMetadataUpdating
                    )}
                    <div className='~text-xs/sm text-gray-600 dark:text-gray-400 truncate'>
                      {url}
                    </div>
                  </div>

                  <div className='flex items-center ~gap-2/3 flex-shrink-0'>
                    <button
                      onClick={() => handlePreviewVideo(url)}
                      className='~p-1.5/2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors'
                      aria-label={t('tabs_revamp.videos_tab.preview_video')}>
                      <PlayCircle
                        styles='~w-4/5 ~h-4/5'
                        fill={COLOR.PRIMARY.LIGHT_900}
                      />
                    </button>
                    <button
                      onClick={() => handleDeleteVideo(url)}
                      className='~p-1.5/2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors'
                      aria-label={t('tabs_revamp.videos_tab.delete_video')}>
                      <TrashOutlined
                        className='~w-4/5 ~h-4/5'
                        fill={COLOR.SEMANTIC.ERROR}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className='flex flex-col items-center justify-center ~py-8/12 ~px-4/6 text-center'>
          <div className='opacity-50 ~mb-2/3'>
            <VideoListIcon
              styles='~w-8/10 ~h-8/10'
              fill={COLOR.PRIMARY.LIGHT_900}
            />
          </div>
          <p className='~text-sm/base text-gray-500 dark:text-gray-400'>
            {t('tabs_revamp.videos_tab.no_videos_added') ||
              'No videos added yet'}
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoList;
